import { AttioSortDirection, SortCriteria } from 'dashboard409a/common/constants/analyst-dashboard';

interface SortState {
  sortBy: string;
  sortOrder: AttioSortDirection;
}

/**
 * Gets the initial sort order from localStorage or returns defaults
 * Supports both old format (single sort) and new format (array of sorts)
 * @param localStorageKey - The key to use in localStorage
 * @param defaultOrderBy - Default orderBy value if not found in localStorage
 * @param defaultOrder - Default order value if not found in localStorage
 * @returns An object with sortBy and sortOrder (for backward compatibility)
 */
export const getInitialSortFromStorage = (
  localStorageKey: string,
  defaultOrderBy: string,
  defaultOrder: AttioSortDirection
): SortState => {
  const localSort = localStorage.getItem(localStorageKey);
  
  if (localSort) {
    try {
      const parsed = JSON.parse(localSort);
      // Support new format with sortCriteria array
      if (parsed.sortCriteria && Array.isArray(parsed.sortCriteria) && parsed.sortCriteria.length > 0) {
        const firstSort = parsed.sortCriteria[0];
        return {
          sortBy: firstSort.property || defaultOrderBy,
          sortOrder: firstSort.direction || defaultOrder,
        };
      }
      // Support old format
      return {
        sortBy: parsed.sortBy || defaultOrderBy,
        sortOrder: parsed.sortOrder || defaultOrder,
      };
    } catch {
      // If parsing fails, return defaults
      return {
        sortBy: defaultOrderBy,
        sortOrder: defaultOrder,
      };
    }
  }
  
  return {
    sortBy: defaultOrderBy,
    sortOrder: defaultOrder,
  };
};

/**
 * Gets the initial sort criteria array from localStorage or returns defaults
 * @param localStorageKey - The key to use in localStorage
 * @param defaultOrderBy - Default orderBy value if not found in localStorage
 * @param defaultOrder - Default order value if not found in localStorage
 * @returns An array of SortCriteria
 */
export const getInitialSortCriteriaFromStorage = (
  localStorageKey: string,
  defaultOrderBy: string,
  defaultOrder: AttioSortDirection
): SortCriteria[] => {
  const localSort = localStorage.getItem(localStorageKey);
  
  if (localSort) {
    try {
      const parsed = JSON.parse(localSort);
      // Support new format with sortCriteria array
      if (parsed.sortCriteria && Array.isArray(parsed.sortCriteria) && parsed.sortCriteria.length > 0) {
        return parsed.sortCriteria;
      }
      // Support old format - convert to array
      if (parsed.sortBy) {
        return [{
          property: parsed.sortBy,
          direction: parsed.sortOrder || defaultOrder,
        }];
      }
    } catch {
      // If parsing fails, return defaults
    }
  }
  
  return [{
    property: defaultOrderBy,
    direction: defaultOrder,
  }];
};

/**
 * Saves sort criteria array to localStorage
 * @param localStorageKey - The key to use in localStorage
 * @param sortCriteria - Array of sort criteria to save
 */
export const saveSortCriteriaToStorage = (
  localStorageKey: string,
  sortCriteria: SortCriteria[]
): void => {
  try {
    localStorage.setItem(localStorageKey, JSON.stringify({ sortCriteria }));
  } catch (error) {
    console.error('Failed to save sort criteria to localStorage:', error);
  }
};

